import React, { Fragment, useMemo } from 'react';
import cx from 'classnames';
import { IFilterItem, ITruncationOption } from '../interfaces';
import { Icons } from '@ems/client-design-system';

export const getCategoryButton = (
  categoryName: string,
  filterItems: IFilterItem[],
  selectedItems: IFilterItem[],
  iconCategories?: string[],
  isCategoryNameRequired?: boolean,
  theme?: Record<string, string>,
  categoryTruncationOptions?: ITruncationOption
) => {
  // Clean the selectedItems list to only include items that are in the filterItems list
  // Memo to store seen warnings to avoid duplicates
  const warningsMemo = useMemo(() => new Set<string>(), []);
  const selectedItemsFiltered = [];

  selectedItems.forEach(selectedItem => {
    const isValid = filterItems.findIndex(({ key }) => key === selectedItem.key) !== -1;
    if (isValid) {
      selectedItemsFiltered.push(selectedItem);
    } else {
      const warningKey = `${categoryName}-${selectedItem.key}`;
      if (!warningsMemo.has(warningKey)) {
        console.warn(
          `Filter warning: Invalid selected item detected with key "${selectedItem.key}" in category "${categoryName}"`
        );
        warningsMemo.add(warningKey);
      }
    }
  });

  let buttonName = <span className="filter_button--category">{categoryName}</span>;
  const selectedItemLength = selectedItemsFiltered.length;

  if (selectedItemLength === 0) {
    if (categoryTruncationOptions && categoryTruncationOptions.empty) {
      buttonName = <span className="filter_button--items">{categoryTruncationOptions.empty}</span>;
    }
    return buttonName;
  }

  const [firstItem] = selectedItemsFiltered;
  const useIcon =
    iconCategories && iconCategories.findIndex(category => category === categoryName) !== -1;
  if (firstItem.icon && useIcon) {
    const sortAlphabetically = (first: { key: string }, second: { key: string }) => {
      if (first.key > second.key) {
        return 1;
      } else if (first.key < second.key) {
        return -1;
      }
      return 0;
    };
    buttonName = (
      <>
        {isCategoryNameRequired && (
          <span className="filter_button--category">{categoryName}: </span>
        )}
        <span className="filter_button--items">
          {selectedItemsFiltered.sort(sortAlphabetically).map(item => {
            const formattedOpString = item.label.toLowerCase().replace(/\s+/g, '');
            const icon = (
              <Icons
                iconName={`ic-${item.icon}`}
                title={item.icon}
                className={cx(item.icon, item.className)}
                size={24}
                style={
                  theme && {
                    fill: theme[formattedOpString],
                    color: theme[formattedOpString],
                  }
                }
              />
            );
            return <Fragment key={item.key}>{icon}</Fragment>;
          })}
        </span>
      </>
    );
  } else {
    if (selectedItemLength >= 1) {
      buttonName = (
        <>
          {isCategoryNameRequired && (
            <span className={cx('filter_button--category', 'filter_button--textonly')}>
              {categoryName}:
            </span>
          )}
          {categoryTruncationOptions && categoryTruncationOptions.all ? (
            <span className="filter_button--items">{categoryTruncationOptions.all}</span>
          ) : (
            <span className="filter_button--items">
              {filterItems[filterItems.findIndex(({ key }) => key === firstItem.key)]
                .labelElement || firstItem.label}{' '}
              {selectedItemLength > 1 && `(+${selectedItemLength - 1})`}
            </span>
          )}
        </>
      );
    }
  }

  return buttonName;
};

export const getNewSelectedItems = (
  item: IFilterItem,
  selectedItems: IFilterItem[],
  type: string
) => {
  if (type === 'single') {
    return getItemsSingle(item, selectedItems);
  }

  return getItemsMulti(item, selectedItems);
};

const getItemsSingle = (item: IFilterItem, selectedItems: IFilterItem[]) => {
  // In a single select, there will only be one element in the array at max,
  // so check the keys directly
  if (selectedItems.length === 0 || selectedItems[0].key !== item.key) {
    return [item];
  }

  return [];
};

const getItemsMulti = (item: IFilterItem, selectedItems: IFilterItem[]) => {
  const indexOfItem: number = selectedItems.findIndex(({ key }) => key === item.key);
  if (indexOfItem === -1) {
    return [...selectedItems, item];
  }

  return [...selectedItems.slice(0, indexOfItem), ...selectedItems.slice(indexOfItem + 1)];
};
